import { cn } from '@pesto/utils';
import JsonView from '@uiw/react-json-view';
import { forwardRef, useMemo } from 'react';

import type { JsonViewerProps } from './JsonViewer.types';

import { darkTheme, lightTheme } from './jsonViewerTheme';

const JsonViewer = forwardRef<HTMLDivElement, JsonViewerProps>(
  (
    {
      data,
      className,
      collapsed = false,
      displayDataTypes = false,
      displayObjectSize = true,
      enableClipboard = true,
      maxHeight,
      resizable = false,
    },
    ref,
  ) => {
    const resolvedTheme = useMemo(() => {
      if (typeof window !== 'undefined') {
        return document.documentElement.classList.contains('dark') ? darkTheme : lightTheme;
      }

      return lightTheme;
    }, []);

    const containerStyle = useMemo(() => {
      const style: React.CSSProperties = {};

      if (maxHeight && resizable) style.height = maxHeight;

      if (maxHeight && !resizable) style.maxHeight = maxHeight;

      return style;
    }, [maxHeight, resizable]);

    const content = (
      <JsonView
        value={data}
        collapsed={collapsed}
        displayDataTypes={displayDataTypes}
        displayObjectSize={displayObjectSize}
        enableClipboard={enableClipboard}
        style={{
          ...resolvedTheme,
          fontSize: '0.875rem',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
        }}
      />
    );

    if (maxHeight) {
      return (
        <div
          ref={ref}
          className={cn('overflow-auto', resizable && 'resize-y min-h-20', className)}
          style={containerStyle}
        >
          {content}
        </div>
      );
    }

    return (
      <div ref={ref} className={cn('overflow-x-auto', className)}>
        {content}
      </div>
    );
  },
);

JsonViewer.displayName = 'JsonViewer';

export { JsonViewer };
